import { Avatar, DropdownMenu, IconButton, Separator, Text, Tooltip } from '@radix-ui/themes';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, LogOut, Menu, Moon, Sun } from 'lucide-react';
import { useConfig } from '@/app/config.tsx';
import { activeNavItem, NAV_ITEMS } from '@/app/nav.ts';
import { useThemeMode } from '@/app/theme.tsx';
import { useToast } from '@/app/toast.tsx';
import { useAuth } from '@/auth/AuthProvider.tsx';
import { cn } from '@/lib/cn.ts';
import { EnvBadge } from './EnvBadge.tsx';

function ThemeToggle() {
  const { appearance, toggle } = useThemeMode();
  const isDark = appearance === 'dark';
  const Icon = isDark ? Sun : Moon;
  return (
    <Tooltip content={isDark ? 'Switch to light' : 'Switch to dark'}>
      <IconButton
        size="2"
        variant="ghost"
        color="gray"
        highContrast
        aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
        onClick={toggle}
      >
        <Icon size={18} />
      </IconButton>
    </Tooltip>
  );
}

export function Header() {
  const { me, logout, hasPermission } = useAuth();
  const config = useConfig();
  const { toast } = useToast();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const items = NAV_ITEMS.filter((item) => !item.permission || hasPermission(item.permission));
  const active = activeNavItem(items, pathname);
  const displayName = me?.user.name || me?.user.email || 'User';

  const onLogout = () => {
    logout().catch(() =>
      toast({ kind: 'error', title: 'Sign out failed', description: 'Try again.' }),
    );
  };

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-[var(--gray-6)] bg-[var(--gray-1)]">
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-[image:var(--brand-gradient)]" />
      <div className="flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Link to="/submissions" className="flex min-w-[7rem] flex-col no-underline">
            <span className="bg-[image:var(--brand-gradient)] bg-clip-text font-[family-name:var(--heading-font-family)] text-lg leading-tight font-bold whitespace-nowrap text-transparent">
              Ops Desk
            </span>
            <span className="text-xs leading-tight text-[var(--gray-9)]">Autopilot operations</span>
          </Link>

          <Separator orientation="vertical" size="2" className="hidden sm:block" />

          {/* Mobile: dropdown menu */}
          <div className="sm:hidden">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <button
                  type="button"
                  className="focus-ring flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-[var(--gray-11)] transition-colors hover:bg-[var(--gray-3)]"
                >
                  <Menu size={18} aria-hidden="true" />
                  {active ? <span>{active.label}</span> : <span>Menu</span>}
                  <ChevronDown size={14} className="text-[var(--gray-9)]" aria-hidden="true" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start" size="2" className="mobile-nav-dropdown">
                {items.map((item) => (
                  <DropdownMenu.Item key={item.label} asChild>
                    <Link to={item.to} className="flex items-center gap-3 py-3 text-base">
                      <item.icon size={18} aria-hidden="true" />
                      {item.label}
                    </Link>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </div>

          {/* Desktop: inline tabs */}
          <nav aria-label="Main" className="hidden items-center gap-1 sm:flex">
            {items.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                aria-current={active === item ? 'page' : undefined}
                className={cn(
                  'focus-ring flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium no-underline transition-colors',
                  active === item
                    ? 'bg-[var(--accent-3)] text-[var(--accent-11)]'
                    : 'text-[var(--gray-11)] hover:bg-[var(--gray-3)] hover:text-[var(--gray-12)]',
                )}
              >
                <item.icon size={16} aria-hidden="true" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <EnvBadge env={me?.env ?? config.env} />
          <ThemeToggle />
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <button
                type="button"
                aria-label="Account menu"
                className="focus-ring flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-[var(--gray-3)]"
              >
                <Avatar size="2" radius="full" fallback={displayName.charAt(0).toUpperCase()} />
                <Text size="2" className="hidden text-[var(--gray-12)] sm:block">
                  {displayName}
                </Text>
                <ChevronDown size={16} className="text-[var(--gray-9)]" aria-hidden="true" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Item disabled>
                <Text size="2">{me?.user.email}</Text>
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item color="red" onClick={onLogout}>
                <LogOut size={14} aria-hidden="true" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </div>
      </div>
    </header>
  );
}
